import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { chatService } from '../services/chatService';
import { ChatSession } from '../types/chat';
import PageHeader from '../components/PageHeader';
import Pagination from '../components/Pagination';
import ConfirmModal from '../components/ConfirmModal';
import './ChatsPage.css';

const ChatsPage: React.FC = () => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creatingSession, setCreatingSession] = useState(false);
  const hasFetchedRef = useRef(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  // Delete confirmation modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<{
    id: string;
    title: string;
  } | null>(null);

  const { user, token, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      loadSessions();
    }
  }, [token, currentPage, pageSize]);

  const loadSessions = async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError('');
      const response = await chatService.getChatSessions(token, currentPage, pageSize);

      // Handle response structure
      if (response.chats) {
        setSessions(response.chats);
      }

      // Update pagination info
      if (response.pagination) {
        setTotalPages(response.pagination.total_pages);
        setTotalItems(response.pagination.total_items);
        setHasNext(response.pagination.has_next);
        setHasPrevious(response.pagination.has_previous);
      }
    } catch (err) {
      setError('Failed to load chat sessions');
      console.error('Error loading sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewChat = async () => {
    if (!token || creatingSession) return;
    
    try {
      setCreatingSession(true);
      setError('');
      const newSession = await chatService.createChatSession(token);
      navigate(`/chat/${newSession.session.id}`);
    } catch (err) {
      setError('Failed to create new chat');
      console.error('Error creating session:', err);
    } finally {
      setCreatingSession(false);
    }
  };

  const handleDeleteClick = (sessionId: string, sessionTitle: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSessionToDelete({ id: sessionId, title: sessionTitle });
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!token || !sessionToDelete) return;

    try {
      await chatService.deleteChatSession(token, sessionToDelete.id);
      // Close modal and reload sessions
      setShowDeleteConfirm(false);
      setSessionToDelete(null);
      loadSessions();
    } catch (err) {
      setError('Failed to delete chat session');
      console.error('Error deleting session:', err);
      setShowDeleteConfirm(false);
      setSessionToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setSessionToDelete(null);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  if (loading) {
    return (
      <div className="chats-container">
        <div className="loading">Loading your chats...</div>
      </div>
    );
  }

  return (
    <div className="chats-container">
      <PageHeader
        title="AI Chat"
        username={user?.username || "User"}
        isSuperuser={user?.is_superuser}
        onLogout={async () => {
          await logout();
          navigate("/login");
        }}
        onManageKnowledge={() => navigate("/knowledge-documents")}
        onManageUsers={() => navigate("/manage-users")}
      />

      <main className="chats-main">
        <div className="chats-actions">
          <button 
            onClick={handleCreateNewChat}
            disabled={creatingSession}
            className="new-chat-button"
          >
            {creatingSession ? 'Creating...' : '+ New Chat'}
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="chats-list">
          {sessions.length === 0 ? (
            <div className="empty-state">
              <h3>No chats yet</h3>
              <p>Start a new conversation with your AI assistant</p>
              <button 
                onClick={handleCreateNewChat}
                disabled={creatingSession}
                className="new-chat-button-primary"
              >
                Start Your First Chat
              </button>
            </div>
          ) : (
            sessions.map((session) => (
              <div
                key={session.id}
                className="chat-item"
                onClick={() => navigate(`/chat/${session.id}`)}
              >
                <div className="chat-item-content">
                  <h3 className="chat-title">{session.title}</h3>
                  <p className="chat-date">{formatDate(session.last_activity)}</p>
                  {session.message_count !== undefined && (
                    <p className="chat-message-count">
                      {session.message_count} messages
                    </p>
                  )}
                </div>
                <div className="chat-item-actions">
                  <button
                    onClick={(e) => handleDeleteClick(session.id, session.title, e)}
                    className="delete-button"
                    title="Delete chat"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {sessions.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            hasNext={hasNext}
            hasPrevious={hasPrevious}
            onPageChange={handlePageChange}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            totalItems={totalItems}
          />
        )}
      </main>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Chat"
        message={`Are you sure you want to delete "${sessionToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        variant="danger"
      />
    </div>
  );
};

export default ChatsPage;